// PortfolioWorkspace — Bankroll, bets, and risk controls in a single workspace.
// Consolidates BankrollPage + PortfolioPage into the OS workspace pattern.

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  BriefcaseBusiness,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Minus,
  Clock,
  ArrowDownCircle,
  ArrowUpCircle,
  RotateCcw,
  Settings2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  DollarSign,
  BarChart2,
  Target,
  Zap,
  Brain,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
  BarChart, Bar, Cell,
} from 'recharts';
import { useAppStore } from '../../store/useAppStore';
import { bankrollApi } from '../../api/client';
import type { BankrollSnapshot, Bet, PerformanceMetrics, CalibrationMetrics } from '../../types';
import { formatCurrency, formatPct, formatOdds } from '../../utils/format';

// ── Helpers ───────────────────────────────────────────────────────────────────

function pnlColor(v: number) {
  return v > 0 ? 'text-win' : v < 0 ? 'text-loss' : 'text-gray-400';
}

function statusIcon(result: Bet['result']) {
  if (result === 'won')     return <CheckCircle className="w-3 h-3 text-win flex-shrink-0" />;
  if (result === 'lost')    return <XCircle className="w-3 h-3 text-loss flex-shrink-0" />;
  if (result === 'push')    return <Minus className="w-3 h-3 text-gray-500 flex-shrink-0" />;
  if (result === 'void')    return <Minus className="w-3 h-3 text-gray-600 flex-shrink-0" />;
  if (result === 'cashout') return <CheckCircle className="w-3 h-3 text-warn flex-shrink-0" />;
  return <Clock className="w-3 h-3 text-gray-600 flex-shrink-0" />;
}

function buildChartData(snapshots: BankrollSnapshot[]) {
  const sorted = [...snapshots].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  return sorted.map((s, i) => ({
    date: new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    balance: s.balance,
    prevBalance: i > 0 ? sorted[i - 1].balance : null,
  }));
}

// ── KPI strip ─────────────────────────────────────────────────────────────────

function KpiStrip({
  bankroll, startingBankroll, exposure, potentialReturn, dailyLoss, dailyLossLimit,
}: {
  bankroll: number;
  startingBankroll: number;
  exposure: number;
  potentialReturn: number;
  dailyLoss: number;
  dailyLossLimit: number;
}) {
  const pnl = bankroll - startingBankroll;
  const roi = startingBankroll > 0 ? pnl / startingBankroll : 0;
  const exposurePct = bankroll > 0 ? exposure / bankroll : 0;
  const isHighExposure = exposurePct >= 0.20;
  const dailyLossNear = dailyLossLimit > 0 && dailyLoss > dailyLossLimit * 0.8;

  return (
    <div className="flex items-stretch border-b border-surface-4 flex-shrink-0 bg-surface-1/30 overflow-x-auto scrollbar-hide">
      {[
        {
          label: 'Total P&L',
          value: `${pnl >= 0 ? '+' : ''}${formatCurrency(pnl)}`,
          sub: `${roi >= 0 ? '+' : ''}${formatPct(Math.abs(roi))} ROI`,
          color: pnlColor(pnl),
        },
        {
          label: 'Bankroll',
          value: formatCurrency(bankroll),
          sub: `Started ${formatCurrency(startingBankroll)}`,
          color: 'text-gray-100',
        },
        {
          label: 'Open Exposure',
          value: formatCurrency(exposure),
          sub: `${formatPct(exposurePct)} of bankroll`,
          color: isHighExposure ? 'text-loss' : exposurePct >= 0.10 ? 'text-warn' : 'text-gray-100',
        },
        {
          label: 'Potential Return',
          value: formatCurrency(potentialReturn),
          sub: 'If all open bets win',
          color: 'text-win',
        },
        {
          label: 'Daily Loss',
          value: formatCurrency(dailyLoss),
          sub: `Limit ${formatCurrency(dailyLossLimit)}`,
          color: dailyLossNear ? 'text-loss' : 'text-gray-100',
        },
      ].map(({ label, value, sub, color }) => (
        <div
          key={label}
          className="flex flex-col gap-0.5 px-4 py-2.5 border-r border-surface-4 last:border-r-0 min-w-[110px] flex-shrink-0"
        >
          <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-600 leading-none">{label}</span>
          <span className={clsx('text-base font-bold font-mono tabular-nums leading-none mt-0.5', color)}>{value}</span>
          <span className="text-[9px] font-mono text-gray-600 leading-none">{sub}</span>
        </div>
      ))}
    </div>
  );
}

// ── Equity chart ──────────────────────────────────────────────────────────────

function EquityChart({ snapshots, startingBankroll }: { snapshots: BankrollSnapshot[]; startingBankroll: number }) {
  const data = buildChartData(snapshots);

  if (data.length < 2) {
    return (
      <div className="flex items-center justify-center py-10 text-xs text-gray-600">
        Log more bets to see your equity curve.
      </div>
    );
  }

  const isPositive = data[data.length - 1].balance >= startingBankroll;
  const lineColor = isPositive ? '#22c55e' : '#ef4444';

  return (
    <ResponsiveContainer width="100%" height={160}>
      <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <defs>
          <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={lineColor} stopOpacity={0.18} />
            <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#4b5563' }} tickLine={false} axisLine={false} />
        <YAxis
          tick={{ fontSize: 9, fill: '#4b5563' }} tickLine={false} axisLine={false} width={52}
          tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const bal = payload[0]?.value as number;
            const prev = (payload[0]?.payload as { prevBalance: number | null })?.prevBalance;
            const dayDiff = prev != null ? bal - prev : null;
            return (
              <div style={{ background: '#0c1220', border: '1px solid #1f2d3d', borderRadius: 4, fontSize: 11 }} className="p-2 space-y-0.5">
                <div className="text-gray-500">{label}</div>
                <div className="text-gray-200 font-mono font-semibold">{formatCurrency(bal)}</div>
                {dayDiff != null && (
                  <div className={clsx('font-mono text-[10px]', dayDiff >= 0 ? 'text-win' : 'text-loss')}>
                    {dayDiff >= 0 ? '+' : ''}{formatCurrency(dayDiff)} vs prev
                  </div>
                )}
                <div className={clsx('font-mono text-[10px]', bal >= startingBankroll ? 'text-win' : 'text-loss')}>
                  {bal >= startingBankroll ? '+' : ''}{formatCurrency(bal - startingBankroll)} vs start
                </div>
              </div>
            );
          }}
        />
        <ReferenceLine y={startingBankroll} stroke="#374151" strokeDasharray="4 2" />
        <Area type="monotone" dataKey="balance" stroke={lineColor} strokeWidth={1.5} dot={false} fill="url(#eqGrad)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Transactions panel ────────────────────────────────────────────────────────

type TxStatus = 'idle' | 'saving' | 'success' | 'error';
type ResetStatus = 'idle' | 'confirm' | 'saving' | 'success' | 'error';

function TransactionPanel({ currentBankroll, onSuccess }: { currentBankroll: number; onSuccess: () => void }) {
  const [tab, setTab] = useState<'deposit' | 'withdraw' | 'reset'>('deposit');
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState<TxStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [resetAmount, setResetAmount] = useState('');
  const [resetStatus, setResetStatus] = useState<ResetStatus>('idle');
  const [resetError, setResetError] = useState('');

  const handleSubmit = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return;
    if (tab === 'withdraw' && val > currentBankroll) {
      setStatus('error');
      setErrorMsg('Exceeds current bankroll');
      return;
    }
    setStatus('saving');
    setErrorMsg('');
    try {
      if (tab === 'deposit') await bankrollApi.deposit(val);
      else await bankrollApi.withdraw(val);
      onSuccess();
      setAmount('');
      setStatus('success');
      setTimeout(() => setStatus('idle'), 2500);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed');
    }
  };

  const handleReset = async () => {
    const val = parseFloat(resetAmount);
    if (!val || val <= 0) return;
    if (resetStatus !== 'confirm') { setResetStatus('confirm'); return; }
    setResetStatus('saving');
    try {
      await bankrollApi.reset(val);
      onSuccess();
      setResetAmount('');
      setResetStatus('success');
      setTimeout(() => setResetStatus('idle'), 2500);
    } catch (err) {
      setResetStatus('error');
      setResetError(err instanceof Error ? err.message : 'Reset failed');
    }
  };

  return (
    <div className="p-3 space-y-3">
      {/* Tab toggle */}
      <div className="flex rounded-lg overflow-hidden border border-surface-4 bg-surface-2">
        {(['deposit', 'withdraw', 'reset'] as const).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setStatus('idle'); setAmount(''); setErrorMsg(''); setResetStatus('idle'); setResetAmount(''); setResetError(''); }}
            className={clsx(
              'flex-1 py-1.5 text-[10px] font-semibold uppercase tracking-wide transition-colors',
              tab === t
                ? t === 'deposit' ? 'bg-win/15 text-win' : t === 'withdraw' ? 'bg-warn/15 text-warn' : 'bg-loss/15 text-loss'
                : 'text-gray-500 hover:text-gray-300',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab !== 'reset' ? (
        <>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-600 font-mono">$</span>
              <input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setStatus('idle'); setErrorMsg(''); }}
                className="w-full pl-6 pr-3 py-1.5 bg-surface-3 border border-surface-4 rounded text-xs font-mono text-gray-200 placeholder-gray-600 focus:outline-none focus:border-brand"
              />
            </div>
            <button
              onClick={() => void handleSubmit()}
              disabled={!amount || status === 'saving'}
              className={clsx(
                'px-3 py-1.5 rounded text-xs font-semibold transition-colors disabled:opacity-40',
                tab === 'deposit'
                  ? 'bg-win/20 text-win border border-win/30 hover:bg-win/30'
                  : 'bg-warn/20 text-warn border border-warn/30 hover:bg-warn/30',
              )}
            >
              {status === 'saving' ? '…' : tab === 'deposit' ? 'Deposit' : 'Withdraw'}
            </button>
          </div>
          {status === 'success' && <p className="text-[10px] text-win flex items-center gap-1"><CheckCircle className="w-3 h-3" />{tab === 'deposit' ? 'Deposit' : 'Withdrawal'} successful</p>}
          {status === 'error' && <p className="text-[10px] text-loss flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errorMsg}</p>}
        </>
      ) : (
        <>
          <div className="flex items-start gap-2 p-2 rounded border border-warn/20 bg-warn/5">
            <AlertTriangle className="w-3 h-3 text-warn flex-shrink-0 mt-0.5" />
            <p className="text-[9px] text-warn/80">Sets a new starting amount. Bet history is preserved.</p>
          </div>
          {resetStatus === 'confirm' || resetStatus === 'saving' ? (
            <div className="space-y-2">
              <p className="text-[10px] text-gray-400">Reset to <span className="font-mono font-bold text-gray-200">{formatCurrency(parseFloat(resetAmount || '0'))}</span>? This cannot be undone.</p>
              <div className="flex gap-2">
                <button onClick={() => setResetStatus('idle')} className="flex-1 py-1.5 text-xs font-medium rounded bg-surface-3 border border-surface-4 text-gray-300 hover:bg-surface-4 transition-colors">Cancel</button>
                <button onClick={() => void handleReset()} disabled={resetStatus === 'saving'} className="flex-1 py-1.5 text-xs font-semibold rounded bg-loss/20 border border-loss/30 text-loss hover:bg-loss/30 transition-colors disabled:opacity-40">
                  {resetStatus === 'saving' ? 'Resetting…' : 'Confirm Reset'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="New starting amount…"
                value={resetAmount}
                onChange={(e) => { setResetAmount(e.target.value); setResetStatus('idle'); setResetError(''); }}
                className="flex-1 bg-surface-3 border border-surface-4 rounded px-3 py-1.5 text-xs text-gray-200 font-mono focus:outline-none focus:border-warn"
              />
              <button
                onClick={() => void handleReset()}
                disabled={!resetAmount}
                className="px-3 py-1.5 rounded text-xs font-semibold bg-warn/20 text-warn border border-warn/30 hover:bg-warn/30 transition-colors disabled:opacity-40"
              >
                Set
              </button>
            </div>
          )}
          {resetStatus === 'success' && <p className="text-[10px] text-win flex items-center gap-1"><CheckCircle className="w-3 h-3" />Bankroll reset successfully</p>}
          {resetStatus === 'error' && <p className="text-[10px] text-loss flex items-center gap-1"><AlertCircle className="w-3 h-3" />{resetError}</p>}
        </>
      )}
    </div>
  );
}

// ── Bet row ───────────────────────────────────────────────────────────────────

function BetRow({ bet }: { bet: Bet }) {
  const pnl = bet.pnl ?? 0;
  const isOpen = !bet.result;
  const date = new Date(bet.gameDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const teams = [bet.awayTeam, bet.homeTeam].filter(Boolean).join(' @ ');

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60 hover:bg-surface-1/40 transition-colors group">
      <div className="flex items-center gap-1.5 w-20 flex-shrink-0">
        {statusIcon(bet.result)}
        <span className="text-[10px] text-gray-600 font-mono tabular-nums">{date}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-300 truncate">{teams || bet.selection}</p>
        <p className="text-[9px] text-gray-600 truncate">{bet.market} · {bet.selection}</p>
      </div>
      <span className="text-[10px] font-mono text-gray-500 tabular-nums flex-shrink-0">{formatOdds(bet.odds)}</span>
      <span className="text-[10px] font-mono text-gray-400 tabular-nums flex-shrink-0">{formatCurrency(bet.stakeAmount)}</span>
      {isOpen ? (
        <span className="text-[9px] font-bold text-info uppercase tracking-wide w-14 text-right flex-shrink-0">Open</span>
      ) : (
        <span className={clsx('text-[10px] font-mono font-bold tabular-nums w-14 text-right flex-shrink-0', pnlColor(pnl))}>
          {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)}
        </span>
      )}
    </div>
  );
}

// ── Risk control display ──────────────────────────────────────────────────────

function RiskRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-surface-4/50 last:border-0">
      <span className="text-[10px] font-medium text-gray-500">{label}</span>
      <span className="text-xs font-mono font-semibold text-gray-200">{value}</span>
    </div>
  );
}

// ── Analytics tab ─────────────────────────────────────────────────────────────

function StatCell({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="flex flex-col gap-1 p-3 rounded-xl border border-surface-4 bg-surface-1">
      <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
      <span className={clsx('text-lg font-bold font-mono tabular-nums leading-none', color ?? 'text-gray-200')}>{value}</span>
      {sub && <span className="text-[9px] font-mono text-gray-600 leading-none">{sub}</span>}
    </div>
  );
}

function AnalyticsTab({ analytics, calibration }: { analytics: PerformanceMetrics | null; calibration: CalibrationMetrics | null }) {
  if (!analytics && !calibration) {
    return (
      <div className="flex items-center justify-center py-16 text-xs text-gray-600">
        Loading analytics…
      </div>
    );
  }

  const a = analytics;

  // Build market chart data
  const marketData = a
    ? Object.entries(a.profitByMarket)
        .filter(([, v]) => v.bets > 0)
        .map(([market, v]) => ({ market: market.replace(/_/g, ' '), bets: v.bets, profit: v.profit, roi: +(v.roi * 100).toFixed(1) }))
        .sort((x, y) => y.roi - x.roi)
    : [];

  // Build confidence chart data
  const confData = a
    ? Object.entries(a.profitByConfidence)
        .filter(([, v]) => v.bets > 0)
        .map(([tier, v]) => ({ tier, bets: v.bets, profit: v.profit, roi: +(v.roi * 100).toFixed(1) }))
        .sort((x, y) => x.tier.localeCompare(y.tier))
    : [];

  const clvBiasColor = a?.clvBias === 'positive' ? 'text-win' : a?.clvBias === 'negative' ? 'text-loss' : 'text-gray-400';
  const clvBiasLabel = a?.clvBias === 'positive' ? 'Beating Market' : a?.clvBias === 'negative' ? 'Below Market' : 'Neutral';

  const verdictColor = (v: string) =>
    v === 'adding_value' ? 'bg-win/15 border-win/30 text-win'
    : v === 'may_be_noise' ? 'bg-loss/15 border-loss/30 text-loss'
    : 'bg-surface-3 border-surface-4 text-gray-500';

  const verdictLabel = (v: string) =>
    v === 'adding_value' ? 'Adding Value' : v === 'may_be_noise' ? 'Noise' : 'Neutral';

  const biasChipColor = (b: string) =>
    b === 'well_calibrated' ? 'bg-win/10 border-win/30 text-win'
    : b === 'overconfident' ? 'bg-loss/10 border-loss/30 text-loss'
    : 'bg-warn/10 border-warn/30 text-warn';

  return (
    <div className="p-4 space-y-4">
      {/* ── KPI grid ── */}
      {a && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCell
            label="Win Rate"
            value={a.settledBets > 0 ? formatPct(a.winRate) : '—'}
            sub={a.settledBets > 0 ? `${a.wins}W · ${a.losses}L · ${a.pushes}P` : 'No settled bets'}
            color={a.winRate >= 0.5 ? 'text-win' : a.winRate > 0 ? 'text-warn' : 'text-gray-400'}
          />
          <StatCell
            label="ROI"
            value={a.settledBets > 0 ? `${a.roi >= 0 ? '+' : ''}${formatPct(a.roi)}` : '—'}
            sub="Return on investment"
            color={pnlColor(a.roi)}
          />
          <StatCell
            label="Avg Odds"
            value={a.settledBets > 0 ? formatOdds(a.avgOdds) : '—'}
            sub="Entry odds average"
          />
          <StatCell
            label="Avg Edge"
            value={a.avgEdge != null && a.avgEdge !== 0 ? `${a.avgEdge >= 0 ? '+' : ''}${formatPct(a.avgEdge)}` : '—'}
            sub="Model edge at entry"
            color={a.avgEdge > 0 ? 'text-win' : a.avgEdge < 0 ? 'text-loss' : 'text-gray-400'}
          />
          <StatCell
            label="Avg CLV"
            value={a.closingLineValue != null && a.closingLineValue !== 0 ? `${a.closingLineValue >= 0 ? '+' : ''}${formatPct(a.closingLineValue)}` : '—'}
            sub={clvBiasLabel}
            color={clvBiasColor}
          />
          <StatCell
            label="Max Drawdown"
            value={a.maxDrawdown > 0 ? `-${formatPct(a.maxDrawdown)}` : '—'}
            sub={a.currentDrawdown > 0 ? `Current: -${formatPct(a.currentDrawdown)}` : 'None active'}
            color={a.maxDrawdown > 0.1 ? 'text-loss' : a.maxDrawdown > 0 ? 'text-warn' : 'text-gray-400'}
          />
        </div>
      )}

      {/* ── Profit by market ── */}
      {marketData.length > 0 && (
        <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
            <BarChart2 className="w-3.5 h-3.5 text-brand flex-shrink-0" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Profit by Market</p>
          </div>
          <div className="p-3 space-y-3">
            <ResponsiveContainer width="100%" height={Math.max(60, marketData.length * 28)}>
              <BarChart data={marketData} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 60 }}>
                <XAxis type="number" tick={{ fontSize: 9, fill: '#4b5563' }} tickLine={false} axisLine={false} unit="%" />
                <YAxis dataKey="market" type="category" tick={{ fontSize: 9, fill: '#6b7280' }} tickLine={false} axisLine={false} width={56} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload as (typeof marketData)[0];
                    return (
                      <div style={{ background: '#0c1220', border: '1px solid #1f2d3d', borderRadius: 4, fontSize: 11 }} className="p-2 space-y-0.5">
                        <div className="text-gray-300 font-semibold capitalize">{d.market}</div>
                        <div className="text-gray-400">{d.bets} bets · {formatCurrency(d.profit)} profit</div>
                        <div className={clsx('font-mono font-bold', d.roi >= 0 ? 'text-win' : 'text-loss')}>{d.roi >= 0 ? '+' : ''}{d.roi}% ROI</div>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="roi" radius={[0, 2, 2, 0]} maxBarSize={14}>
                  {marketData.map((entry, i) => (
                    <Cell key={i} fill={entry.roi >= 0 ? '#22c55e' : '#ef4444'} fillOpacity={0.7} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Compact table */}
            <div className="border-t border-surface-4/50 pt-2 space-y-0">
              <div className="flex items-center gap-2 pb-1">
                <span className="text-[9px] uppercase tracking-wide text-gray-700 flex-1">Market</span>
                <span className="text-[9px] uppercase tracking-wide text-gray-700 w-10 text-right">Bets</span>
                <span className="text-[9px] uppercase tracking-wide text-gray-700 w-16 text-right">Profit</span>
                <span className="text-[9px] uppercase tracking-wide text-gray-700 w-12 text-right">ROI</span>
              </div>
              {marketData.map((d) => (
                <div key={d.market} className="flex items-center gap-2 py-1 border-t border-surface-4/30">
                  <span className="text-[10px] text-gray-400 flex-1 capitalize">{d.market}</span>
                  <span className="text-[10px] font-mono text-gray-500 w-10 text-right">{d.bets}</span>
                  <span className={clsx('text-[10px] font-mono w-16 text-right', pnlColor(d.profit))}>{formatCurrency(d.profit)}</span>
                  <span className={clsx('text-[10px] font-mono font-bold w-12 text-right', d.roi >= 0 ? 'text-win' : 'text-loss')}>{d.roi >= 0 ? '+' : ''}{d.roi}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Profit by confidence tier ── */}
      {confData.length > 0 && (
        <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
            <Target className="w-3.5 h-3.5 text-brand flex-shrink-0" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Profit by Confidence</p>
          </div>
          <div className="p-3">
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={confData} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
                <XAxis dataKey="tier" tick={{ fontSize: 9, fill: '#4b5563' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#4b5563' }} tickLine={false} axisLine={false} unit="%" width={36} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload as (typeof confData)[0];
                    return (
                      <div style={{ background: '#0c1220', border: '1px solid #1f2d3d', borderRadius: 4, fontSize: 11 }} className="p-2 space-y-0.5">
                        <div className="text-gray-300 font-semibold">{d.tier}</div>
                        <div className="text-gray-400">{d.bets} bets</div>
                        <div className={clsx('font-mono font-bold', d.roi >= 0 ? 'text-win' : 'text-loss')}>{d.roi >= 0 ? '+' : ''}{d.roi}% ROI</div>
                      </div>
                    );
                  }}
                />
                <ReferenceLine y={0} stroke="#374151" strokeDasharray="3 2" />
                <Bar dataKey="roi" radius={[2, 2, 0, 0]} maxBarSize={32}>
                  {confData.map((entry, i) => (
                    <Cell key={i} fill={entry.roi >= 0 ? '#22c55e' : '#ef4444'} fillOpacity={0.7} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Model calibration ── */}
      {calibration && (
        <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
            <Brain className="w-3.5 h-3.5 text-brand flex-shrink-0" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Model Calibration</p>
            <span className={clsx('ml-auto px-2 py-0.5 rounded text-[9px] font-semibold border capitalize', biasChipColor(calibration.bias))}>
              {calibration.bias.replace(/_/g, ' ')}
            </span>
          </div>
          <div className="p-3 space-y-3">
            {/* Top metrics */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Brier Score', value: calibration.brierScore.toFixed(3), sub: '< 0.25 beats random', color: calibration.brierScore < 0.25 ? 'text-win' : 'text-warn' },
                { label: 'Calib. Error', value: calibration.ece.toFixed(3), sub: 'Expected calibration', color: calibration.ece < 0.05 ? 'text-win' : calibration.ece < 0.10 ? 'text-warn' : 'text-loss' },
                { label: 'Avg Confidence', value: formatPct(calibration.avgConfidence), sub: `Actual: ${formatPct(calibration.avgWinRate)}`, color: 'text-gray-300' },
              ].map(({ label, value, sub, color }) => (
                <div key={label} className="flex flex-col gap-0.5 p-2 rounded-lg bg-surface-2 border border-surface-4">
                  <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
                  <span className={clsx('text-base font-bold font-mono tabular-nums leading-none', color)}>{value}</span>
                  <span className="text-[9px] text-gray-600 leading-none">{sub}</span>
                </div>
              ))}
            </div>
            {/* Agent verdicts */}
            {calibration.agents.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-600">Agent Performance</p>
                {calibration.agents.map((agent) => (
                  <div key={agent.agentName} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-surface-2 border border-surface-4/50">
                    <Zap className="w-3 h-3 text-gray-600 flex-shrink-0" />
                    <span className="text-[10px] text-gray-400 flex-1 truncate">{agent.agentName}</span>
                    <span className="text-[9px] font-mono text-gray-600">{agent.brierScore.toFixed(3)}</span>
                    <span className={clsx('px-1.5 py-0.5 rounded text-[9px] font-semibold border', verdictColor(agent.verdict))}>
                      {verdictLabel(agent.verdict)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {calibration.sampleCount < 20 && (
              <p className="text-[9px] text-gray-700 italic">
                Calibration improves with more settled bets. Currently {calibration.sampleCount} samples.
              </p>
            )}
          </div>
        </div>
      )}

      {!a && !calibration && (
        <div className="flex items-center justify-center py-10 text-xs text-gray-600">
          No analytics data available yet.
        </div>
      )}
    </div>
  );
}

// ── PortfolioWorkspace ────────────────────────────────────────────────────────

const BETS_PER_PAGE = 30;
type BetFilter = 'all' | 'open' | 'won' | 'lost' | 'push';

export function PortfolioWorkspace() {
  const { bankrollSettings, exposure, bets, bankrollLoading, fetchBankroll, fetchBets } = useAppStore();
  const [tab, setTab] = useState<'bankroll' | 'bets' | 'risk' | 'analytics'>('bankroll');
  const [snapshots, setSnapshots] = useState<BankrollSnapshot[]>([]);
  const [analytics, setAnalytics] = useState<PerformanceMetrics | null>(null);
  const [calibration, setCalibration] = useState<CalibrationMetrics | null>(null);
  const [betFilter, setBetFilter] = useState<BetFilter>('all');
  const [betPage, setBetPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void fetchBankroll();
    void fetchBets();
    bankrollApi.getHistory().then(setSnapshots).catch(() => {});
    bankrollApi.getAnalytics().then(setAnalytics).catch(() => {});
    bankrollApi.getCalibration().then(setCalibration).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchBankroll(), fetchBets()]);
    bankrollApi.getHistory().then(setSnapshots).catch(() => {});
    bankrollApi.getAnalytics().then(setAnalytics).catch(() => {});
    bankrollApi.getCalibration().then(setCalibration).catch(() => {});
    setTimeout(() => setRefreshing(false), 400);
  };

  const bs = bankrollSettings;
  const exp = exposure;

  const pnl = bs ? bs.currentBankroll - bs.startingBankroll : 0;
  const roi = bs && bs.startingBankroll > 0 ? pnl / bs.startingBankroll : 0;

  const filteredBets = bets.filter((b) => {
    if (betFilter === 'all') return true;
    if (betFilter === 'open') return !b.result;
    return b.result === betFilter;
  });

  const totalBetPages = Math.ceil(filteredBets.length / BETS_PER_PAGE);
  const pageBets = filteredBets.slice(betPage * BETS_PER_PAGE, (betPage + 1) * BETS_PER_PAGE);

  const openBets = bets.filter((b) => !b.result);
  const wonBets = bets.filter((b) => b.result === 'won');

  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      {/* Workspace header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-surface-4 bg-surface-1/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gold/10 border border-gold/20">
            <BriefcaseBusiness className="w-3.5 h-3.5 text-gold" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-100 leading-tight">Portfolio</p>
            <p className="text-[9px] text-gray-500 uppercase tracking-widest leading-tight">Bankroll · Exposure · Performance</p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {bs && (
            <span className={clsx(
              'px-2 py-1 rounded-md text-[10px] font-semibold font-mono',
              pnl >= 0 ? 'bg-win/10 border border-win/25 text-win' : 'bg-loss/10 border border-loss/25 text-loss',
            )}>
              {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)} ({roi >= 0 ? '+' : ''}{formatPct(Math.abs(roi))})
            </span>
          )}
          {openBets.length > 0 && (
            <span className="px-2 py-1 rounded-md bg-info/10 border border-info/25 text-[10px] font-semibold text-info">
              {openBets.length} Open
            </span>
          )}
          <button
            onClick={() => void handleRefresh()}
            disabled={bankrollLoading || refreshing}
            className="flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={clsx('w-3.5 h-3.5', (bankrollLoading || refreshing) && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* KPI strip — always visible */}
      {bs && exp && (
        <KpiStrip
          bankroll={bs.currentBankroll}
          startingBankroll={bs.startingBankroll}
          exposure={exp.totalOpen}
          potentialReturn={exp.totalPotentialReturn}
          dailyLoss={exp.dailyLoss}
          dailyLossLimit={exp.dailyLossLimit}
        />
      )}

      {/* Tab bar */}
      <div className="flex items-center gap-0 border-b border-surface-4 flex-shrink-0 bg-surface-1/20">
        {([
          { key: 'bankroll', label: 'Bankroll', icon: TrendingUp },
          { key: 'bets', label: `Bets (${bets.length})`, icon: null },
          { key: 'risk', label: 'Risk Controls', icon: Settings2 },
          { key: 'analytics', label: 'Analytics', icon: BarChart2 },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key as typeof tab)}
            className={clsx(
              'flex items-center gap-1.5 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest border-b-2 transition-colors',
              tab === key
                ? 'border-gold text-gold'
                : 'border-transparent text-gray-500 hover:text-gray-300',
            )}
          >
            {Icon && <Icon className="w-3 h-3" />}
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* ── Bankroll tab ──────────────────────────────────────────────────── */}
        {tab === 'bankroll' && (
          <div className="p-4 space-y-4">
            {/* Stats overview */}
            {bs && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total Bets', value: String(bets.length), color: 'text-gray-200' },
                  { label: 'Won', value: String(wonBets.length), color: 'text-win' },
                  { label: 'Open Positions', value: String(openBets.length), color: 'text-info' },
                  {
                    label: 'Win Rate',
                    value: bets.filter((b) => b.result === 'won' || b.result === 'lost').length > 0
                      ? formatPct(wonBets.length / bets.filter((b) => b.result === 'won' || b.result === 'lost').length)
                      : '—',
                    color: 'text-brand',
                  },
                ].map(({ label, value, color }) => (
                  <div key={label} className="flex flex-col gap-1 p-3 rounded-xl border border-surface-4 bg-surface-1">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
                    <span className={clsx('text-xl font-bold font-mono tabular-nums leading-none', color)}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Equity curve */}
            <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
                <TrendingUp className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Equity Curve</p>
                {exp?.isAtRisk && (
                  <span className="flex items-center gap-1 ml-auto text-[9px] text-loss font-semibold">
                    <AlertTriangle className="w-3 h-3" />At Risk
                  </span>
                )}
              </div>
              <div className="p-2">
                <EquityChart snapshots={snapshots} startingBankroll={bs?.startingBankroll ?? 0} />
              </div>
            </div>

            {/* Violations / warnings */}
            {exp && exp.violations.length > 0 && (
              <div className="rounded-xl border border-loss/30 bg-loss/5 p-3 space-y-1.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-loss/80">Risk Violations</p>
                {exp.violations.map((v, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-loss flex-shrink-0 mt-0.5" />
                    <p className="text-[10px] text-loss/80">{v}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Transactions */}
            <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
                <DollarSign className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Transactions</p>
              </div>
              {bs ? (
                <TransactionPanel currentBankroll={bs.currentBankroll} onSuccess={() => void fetchBankroll()} />
              ) : (
                <div className="flex items-center justify-center py-8 text-xs text-gray-600">Loading bankroll data…</div>
              )}
            </div>
          </div>
        )}

        {/* ── Bets tab ─────────────────────────────────────────────────────── */}
        {tab === 'bets' && (
          <div className="flex flex-col h-full">
            {/* Filter strip */}
            <div className="flex items-center gap-1 px-3 py-2 border-b border-surface-4 flex-shrink-0">
              {(['all', 'open', 'won', 'lost', 'push'] as BetFilter[]).map((f) => {
                const count = f === 'all' ? bets.length
                  : f === 'open' ? bets.filter((b) => !b.result).length
                  : bets.filter((b) => b.result === f).length;
                return (
                  <button
                    key={f}
                    onClick={() => { setBetFilter(f); setBetPage(0); }}
                    className={clsx(
                      'px-2.5 py-1 text-[10px] font-semibold rounded uppercase tracking-wide transition-colors',
                      betFilter === f
                        ? f === 'won' ? 'bg-win/15 border border-win/40 text-win'
                          : f === 'lost' ? 'bg-loss/15 border border-loss/40 text-loss'
                          : f === 'open' ? 'bg-info/15 border border-info/40 text-info'
                          : 'bg-brand/15 border border-brand/40 text-brand-light'
                        : 'text-gray-600 hover:text-gray-300',
                    )}
                  >
                    {f} <span className="font-mono ml-0.5">{count}</span>
                  </button>
                );
              })}
              <Link
                to="/baseball/mlb/bets"
                className="ml-auto flex items-center gap-1 text-[9px] text-gray-600 hover:text-gray-300 transition-colors"
              >
                Full History <ExternalLink className="w-2.5 h-2.5" />
              </Link>
            </div>

            {/* Bet list */}
            <div className="flex-1 overflow-y-auto">
              {pageBets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-xs text-gray-600">No {betFilter !== 'all' ? betFilter : ''} bets yet.</p>
                </div>
              ) : (
                <>
                  {/* Column headers */}
                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-surface-4/40 bg-surface-1/20">
                    <span className="text-[9px] uppercase tracking-wide text-gray-700 w-20 flex-shrink-0">Date</span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-700 flex-1">Game / Market</span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-700 flex-shrink-0 w-12 text-right">Odds</span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-700 flex-shrink-0 w-12 text-right">Stake</span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-700 flex-shrink-0 w-14 text-right">P&L</span>
                  </div>
                  {pageBets.map((bet) => <BetRow key={bet.id} bet={bet} />)}
                </>
              )}
            </div>

            {/* Pagination */}
            {totalBetPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-3 py-2 border-t border-surface-4 flex-shrink-0">
                <button
                  onClick={() => setBetPage((p) => Math.max(0, p - 1))}
                  disabled={betPage === 0}
                  className="p-1 rounded text-gray-500 hover:text-gray-200 disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-[10px] text-gray-500 font-mono">
                  {betPage + 1} / {totalBetPages}
                </span>
                <button
                  onClick={() => setBetPage((p) => Math.min(totalBetPages - 1, p + 1))}
                  disabled={betPage === totalBetPages - 1}
                  className="p-1 rounded text-gray-500 hover:text-gray-200 disabled:opacity-30 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Analytics tab ─────────────────────────────────────────────────── */}
        {tab === 'analytics' && (
          <AnalyticsTab analytics={analytics} calibration={calibration} />
        )}

        {/* ── Risk Controls tab ─────────────────────────────────────────────── */}
        {tab === 'risk' && (
          <div className="p-4 space-y-4">
            {bs ? (
              <>
                <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
                    <Settings2 className="w-3.5 h-3.5 text-gray-500" />
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Staking</p>
                  </div>
                  <div className="p-3">
                    <RiskRow label="Unit Size" value={formatCurrency(bs.unitSize)} />
                    <RiskRow label="Staking Method" value={bs.stakingMethod.replace('_', ' ')} />
                    <RiskRow label="Kelly Fraction" value={`${(bs.kellyFraction * 100).toFixed(0)}%`} />
                  </div>
                </div>

                <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
                    <AlertTriangle className="w-3.5 h-3.5 text-warn" />
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Risk Limits</p>
                  </div>
                  <div className="p-3">
                    <RiskRow label="Max Daily Loss" value={`${formatCurrency(bs.maxDailyLoss)} (${formatPct(bs.maxDailyLossPct)})`} />
                    <RiskRow label="Max Bet Size" value={`${formatCurrency(bs.maxBetSize)} (${formatPct(bs.maxBetSizePct)})`} />
                    <RiskRow label="Max Exposure / Game" value={`${formatCurrency(bs.maxExposurePerGame)} (${formatPct(bs.maxExposurePerGamePct)})`} />
                    <RiskRow label="Max Total Exposure" value={`${formatCurrency(bs.maxTotalExposure)} (${formatPct(bs.maxTotalExposurePct)})`} />
                  </div>
                </div>

                {exp && (
                  <div className="rounded-xl border border-surface-4 bg-surface-1 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4/60">
                      <TrendingDown className="w-3.5 h-3.5 text-info" />
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Current Exposure</p>
                    </div>
                    <div className="p-3">
                      <RiskRow label="Total Open" value={formatCurrency(exp.totalOpen)} />
                      <RiskRow label="% of Bankroll" value={formatPct(exp.percentOfBankroll)} />
                      <RiskRow label="Remaining Capacity" value={formatCurrency(exp.remainingCapacity)} />
                      <RiskRow label="Daily Loss Today" value={formatCurrency(exp.dailyLoss)} />
                    </div>
                  </div>
                )}

                <p className="text-[9px] text-gray-700 text-center">
                  To update risk controls, visit{' '}
                  <Link to="/baseball/mlb/bankroll" className="text-brand hover:text-brand-light underline">Bankroll Settings</Link>.
                </p>
              </>
            ) : (
              <div className="flex items-center justify-center py-12 text-xs text-gray-600">
                {bankrollLoading ? 'Loading…' : 'No bankroll configured.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

